import React, { useEffect, useState } from "react"
import { IProject } from "../../../types/project"
import styled from "styled-components"
import { cross } from "../../../assets/index"
import Image from "gatsby-image"
import { marker } from "../../../assets/index"
import Slider from "../../common/Slider"
import { createPortal } from "react-dom"
import media from "../../../helpers/media"

interface IPopin {
  className?: string
  popinTransition: number
}

const Popin = styled.aside<IPopin>`
  z-index: 10;
  position: absolute;
  background: white;
  width: 100%;
  height: 100%;
  right: 0;
  padding: 0 15px;
  overflow-y: auto;
  height: calc(100vh - 50px);

  transform: translateX(100%);
  transition: ${({ popinTransition }: IPopin): string =>
    `transform ${popinTransition / 1000}s ease-in-out, border ${
      popinTransition / 1000
    }s ease-in-out`};

  &.active {
    transform: translateX(0%);
  }

  ${media.mobile} {
    padding: 0 30px;
  }
  ${media.laptop} {
    width: 49%;
  }
`

const Flex = styled.div`
  display: flex;
  align-items: center;
`
const Header = styled.div`
  border-bottom: 1px solid grey;
`

const Title = styled(Flex)`
  align-items: baseline;
  margin-top: 9px;
`

const Description = styled(Flex)`
  padding: 15px 0;
  justify-content: space-between;
  display: block;

  ${media.mobile} {
    display: flex;
  }
`

const Infos = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  padding: 15px 0;
`

const Info = styled(Flex)`
  align-items: baseline;
  margin: 5px 0;
  display: block;

  ${media.mobile} {
    display: flex;
  }
`

const Intro = styled(Flex)`
  position: relative;
  margin: 15px 0;

  p {
    margin: 0;
    padding: 20px 0 0 0;
    border-top: 1px solid grey;
    font-size: var(--font-size-md);
    font-family: var(--font-secondary);
    color: var(--color-grey-2);
  }

  ${media.tablet} {
    p {
      margin: 0 0 0 12%;
    }
  }
`

const Shortname = styled.div`
  font-size: var(--font-size-lg);

  ${media.mobile} {
    font-size: var(--font-size-xl);
  }
`

const Name = styled.div`
  font-size: var(--font-size-lg);
  margin-left: 15px;
  line-height: 1.2;

  ${media.mobile} {
    font-size: var(--font-size-xl);
  }
`

const Label = styled.div`
  ${media.mobile} {
    min-width: 120px;
  }
`

const Value = styled.div`
  font-family: var(--font-secondary);
  color: var(--color-grey-2);
`

interface IClose {
  onClick?: React.Dispatch<React.SetStateAction<IProject>>
}

const Close = styled.button<IClose>`
  position: absolute;
  z-index: 10;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 10px;
  border: none;
  background: #ffffffb3;
  text-transform: uppercase;
  font-size: var(--font-size-xxs);
  color: var(--color-grey-1);
  transform: translateX(50vw);
  transition: transform 0.4s ease-in-out;

  img {
    margin-left: 10px;
    width: 10px;
    height: 10px;
  }

  &:hover {
    color: var(--color-dark);
  }

  &.active {
    transform: translateX(0);
  }
`

const Photo = styled.li`
  margin: 20px 0;
  cursor: pointer;

  &:first-child {
    margin: 0;
  }
`

const PhotoList = styled.ul`
  list-style: none;
  padding: 0;
`

const Place = styled.div`
  display: flex;
  align-items: center;
  font-size: var(--font-size-sm);
  font-family: var(--font-secondary);

  img {
    width: 8px;
    transform: translateY(-2px);
  }

  span {
    margin: 0 0 0 5px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`

const Role = styled.div`
  font-size: var(--font-size-xs);
  /* font-family: var(--font-secondary); */
  /* font-style: italic; */
`

const StyledSlider = styled(Slider)`
  position: fixed;
  z-index: 15;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: var(--color-light);
`

export interface ISliderOpen {
  open: boolean
  selected: number
}

interface IProps {
  project?: IProject
  closePopin: any
  popinTransition: number
  active: boolean
  xs: boolean
}

const PopinProject = ({
  project,
  closePopin,
  popinTransition,
  active,
  xs,
}: IProps) => {
  if (!project) {
    return (
      <Popin
        popinTransition={popinTransition}
        className={active ? "active" : ""}
      >
        Error
      </Popin>
    )
  }

  /**
   * handle SLider wrapper
   */
  const [sliderOpen, setSliderOpen] = useState<ISliderOpen>({
    open: false,
    selected: 0,
  })
  const [target, setTarget] = useState<Element | null>()

  useEffect(() => {
    setTarget(document.querySelector("#mainWrapper"))
  }, [])

  const closeSlider = () =>
    setSliderOpen({
      open: false,
      selected: 0,
    })

  const {
    shortname,
    name,
    region,
    city,
    department,
    role,
    mission,
    surface,
    partners,
    constructionProject,
    amount,
    teamGeorges,
    introduction,
    photos: photosList,
  } = project

  // remove all invalid photos assets
  const photos = photosList?.filter(({ fluid }) => fluid)

  const roles = role?.map(
    item => item[0].toUpperCase() + item.slice(1)
  )

  return (
    <>
      <Popin
        popinTransition={popinTransition}
        className={`popin-projects ${active ? "active" : ""}`}
        id="popin-column"
      >
        <Header>
          <Title>
            <Shortname>{shortname}</Shortname>
            <Name>{name}</Name>
          </Title>
          <Description>
            <Place>
              <img src={marker} alt="Place" />
              <span>
                {region}, {city} ({department})
              </span>
            </Place>
            {roles?.length && <Role>{roles.join(", ")}</Role>}
          </Description>
        </Header>
        <Infos>
          {mission?.mission && (
            <Info>
              <Label>mission</Label>
              <Value>{mission.mission}</Value>
            </Info>
          )}
          {surface && (
            <Info>
              <Label>surface</Label>
              <Value>{surface}</Value>
            </Info>
          )}
          {partners && (
            <Info>
              <Label>partenaires</Label>
              <Value>{partners.join(", ")}</Value>
            </Info>
          )}
          {constructionProject && (
            <Info>
              <Label>{xs ? "maîtrise" : "maîtrise d'ouvrage"}</Label>
              <Value>{constructionProject}</Value>
            </Info>
          )}
          {amount && (
            <Info>
              <Label>montant de l'étude</Label>
              <Value>{amount}</Value>
            </Info>
          )}
          {teamGeorges && teamGeorges.length > 0 && (
            <Info>
              <Label>georges</Label>
              <Value style={{ textTransform: "capitalize" }}>
                {teamGeorges
                  .map(item => `${item.firstName} ${item.name}`)
                  .join(", ")}
              </Value>
            </Info>
          )}
        </Infos>
        {introduction && (
          <Intro>
            <p>{introduction?.introduction}</p>
          </Intro>
        )}
        {photos?.length && (
          <PhotoList>
            {photos?.map((photo: any, i: number) => (
              <Photo
                key={i}
                onClick={() =>
                  setSliderOpen({
                    open: true,
                    selected: i,
                  })
                }
              >
                <Image
                  fluid={photo.fluid}
                  alt={`${name} - aperçu ${i}`}
                />
              </Photo>
            ))}
          </PhotoList>
        )}

        {target &&
          sliderOpen.open &&
          photos?.length &&
          createPortal(
            <StyledSlider
              className="slider-popin"
              photos={photos}
              closeSlider={closeSlider}
              sliderOpen={sliderOpen}
            />,
            target
          )}
      </Popin>
      <Close
        onClick={closePopin}
        id="closePopin"
        className={active ? "active" : ""}
      >
        fermer
        <img src={cross} alt="Fermer" />
      </Close>
    </>
  )
}

export default PopinProject
